import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts, calendlyLink } from '../mock';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const BlogPostPage = () => {
  const { postId } = useParams();
  const post = blogPosts.find(p => p.id === postId);
  const currentIndex = blogPosts.findIndex(p => p.id === postId);
  const nextPost = blogPosts[currentIndex + 1];
  const prevPost = blogPosts[currentIndex - 1];

  if (!post) {
    return (
      <div className="section-padding">
        <div className="container">
          <p className="body-large" style={{ marginBottom: '24px' }}>Article not found</p>
          <Link to="/blog" style={{
            color: 'var(--interactive-base)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ArrowLeft size={14} /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Parse content with markdown-like formatting
  const renderContent = (content) => {
    const paragraphs = content.split('\n\n');
    
    return paragraphs.map((para, index) => {
      // Handle headers
      if (para.startsWith('**') && para.endsWith('**')) {
        const headerText = para.slice(2, -2);
        return (
          <h2 key={index} className="heading-2" style={{ 
            marginTop: '48px', 
            marginBottom: '24px',
            color: 'var(--text-primary)'
          }}>
            {headerText}
          </h2>
        );
      }
      
      // Handle bullet points
      if (para.includes('\n•') || para.startsWith('•')) {
        const items = para.split('\n').filter(item => item.trim());
        return (
          <ul key={index} style={{ 
            listStyle: 'none', 
            padding: 0, 
            marginBottom: '24px' 
          }}>
            {items.map((item, i) => {
              const cleanItem = item.replace('•', '').trim();
              if (!cleanItem) return null;
              return (
                <li key={i} style={{ 
                  marginBottom: '12px', 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px' 
                }}>
                  <span style={{ color: 'var(--interactive-base)', marginTop: '2px' }}>•</span>
                  <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>{cleanItem}</span>
                </li>
              );
            })}
          </ul>
        );
      }
      
      // Regular paragraph - handle inline bold
      const parts = para.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={index} className="body-large" style={{ 
          color: 'var(--text-secondary)', 
          lineHeight: '1.9',
          marginBottom: '24px'
        }}>
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} style={{ color: 'var(--text-primary)' }}>{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div>
      {/* Breadcrumb */}
      <section style={{ padding: '40px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <Link to="/blog" style={{
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }} data-testid="back-to-blog">
            <ArrowLeft size={14} /> Back to Articles
          </Link>
        </div>
      </section>

      {/* Hero Image */}
      <section>
        <img
          src={post.image}
          alt={post.title}
          style={{
            width: '100%',
            height: '450px',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </section>

      {/* Blog Post Content */}
      <section className="section-padding">
        <div className="container">
          <article style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Meta Info */}
            <div style={{ marginBottom: '24px' }}>
              <span className="body-small" style={{ color: 'var(--text-meta)' }}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })} · By {post.author}
              </span>
            </div>

            {/* Title */}
            <h1 className="hero-large" style={{ marginBottom: '16px' }} data-testid="blog-post-title">
              {post.title}
            </h1>

            {/* Subtitle */}
            {post.subtitle && (
              <p className="body-large" style={{
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                marginBottom: '32px',
                paddingBottom: '32px',
                borderBottom: '1px solid var(--border-light)'
              }}>
                {post.subtitle}
              </p>
            )}

            {/* Content */}
            <div data-testid="blog-post-content">
              {renderContent(post.content)}
            </div>

            {/* CTA */}
            <div style={{
              marginTop: '64px',
              padding: '48px',
              background: 'var(--bg-secondary)',
              textAlign: 'center'
            }}>
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>
                Ready to Begin Your Journey?
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                Book a free discovery session and explore how hypnotherapy can support your transformation.
              </p>
              <a 
                href={calendlyLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary"
                style={{
                  background: 'transparent',
                  color: 'var(--interactive-base)',
                  border: '1px solid var(--interactive-base)',
                  padding: '16px 32px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                data-testid="blog-cta-button"
              >
                Book Your Free Session
              </a>
            </div>

            {/* Navigation */}
            <div style={{
              marginTop: '48px',
              paddingTop: '32px',
              borderTop: '1px solid var(--border-light)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '24px'
            }}>
              {prevPost ? (
                <Link to={`/blog/${prevPost.id}`} style={{
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <ArrowLeft size={14} />
                  <span className="body-small">Previous: {prevPost.title}</span>
                </Link>
              ) : <div />}
              
              {nextPost ? (
                <Link to={`/blog/${nextPost.id}`} style={{
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span className="body-small">Next: {nextPost.title}</span>
                  <ArrowRight size={14} />
                </Link>
              ) : <div />}
            </div>
          </article>
        </div>
      </section>

      <style>{`
        .btn-primary:hover {
          background: var(--interactive-base) !important;
          color: var(--bg-primary) !important;
        }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
